import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import {
  auditTrail,
  type AuditRecordWithStates,
  type AuditTrailParams,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { HistoryChangeDialog } from '@/shared/ui/HistoryTab';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';

import { entidadeLegivel, motivoDoConteudoOculto, opcoesDaEntidade } from './rotulos';

/**
 * Aba Auditoria da tela de logs: a trilha da empresa, com quem fez, o que, quando e
 * de onde.
 *
 * O antes e o depois vem do servidor so para quem tem auditoria.trilha.view e le o
 * cadastro alterado. A tela nao decide: mostra o que veio e, quando o conteudo foi
 * omitido, diz por que.
 */

const LIMITE = 25;
const IDENTIFICADOR = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface Filtros {
  from: string;
  to: string;
  actor: string;
  module: string;
  entity_type: string;
  entity_id: string;
}

const filtrosVazios: Filtros = {
  from: '',
  to: '',
  actor: '',
  module: '',
  entity_type: '',
  entity_id: '',
};

export function LogsAuditTab() {
  // Sem a leitura das pessoas, quem fez vem mascarado e a busca por quem fez e
  // recusada pelo servidor, como na aba Acessos (achado B18 da segunda revisao).
  const { can } = useAuth();
  const lePessoas = can('usuarios.usuario.view');
  const [rascunho, setRascunho] = useState<Filtros>(filtrosVazios);
  const [aplicados, setAplicados] = useState<Filtros>(filtrosVazios);
  const [offset, setOffset] = useState(0);
  const [aberta, setAberta] = useState<AuditRecordWithStates | null>(null);

  const identificadorInvalido =
    rascunho.entity_id.trim() !== '' && !IDENTIFICADOR.test(rascunho.entity_id.trim());

  const parametros: AuditTrailParams = {
    limit: LIMITE,
    offset,
    from: aplicados.from,
    to: aplicados.to,
    actor: lePessoas ? aplicados.actor.trim() : '',
    module: aplicados.module.trim(),
    entity_type: aplicados.entity_type,
    entity_id: aplicados.entity_id.trim(),
  };

  const consulta = useQuery({
    queryKey: ['audit-logs', 'logs', parametros],
    queryFn: ({ signal }) => auditTrail.listWithStates(parametros, signal),
    placeholderData: keepPreviousData,
  });

  const muda = (campo: keyof Filtros) => (valor: string) =>
    setRascunho((atual) => ({ ...atual, [campo]: valor }));

  const aplicar = () => {
    if (identificadorInvalido) return;
    setAplicados(rascunho);
    setOffset(0);
  };

  const colunas: Column<AuditRecordWithStates>[] = [
    {
      key: 'quando',
      header: 'Data e hora',
      render: (linha) => (
        <span className="tabular-nums">{formatDateTime(linha.occurred_at)}</span>
      ),
    },
    {
      key: 'quem',
      header: 'Quem',
      render: (linha) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">
            {linha.actor_name || linha.actor_label || 'sem identificacao'}
          </p>
          {linha.actor_name && linha.actor_label ? (
            <p className="truncate text-2xs text-ink-500">{linha.actor_label}</p>
          ) : null}
          {linha.actor_type !== 'human' ? (
            <Badge tone="neutral">{linha.actor_type}</Badge>
          ) : null}
          {linha.actor_masked ? <Badge tone="neutral">mascarado</Badge> : null}
        </div>
      ),
    },
    {
      key: 'acao',
      header: 'Acao',
      render: (linha) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{linha.action}</p>
          <p className="truncate text-2xs text-ink-500">
            {entidadeLegivel(linha.entity_type)}
          </p>
        </div>
      ),
    },
    {
      key: 'modulo',
      header: 'Modulo',
      hideBelow: 'md',
      render: (linha) => linha.module,
    },
    {
      key: 'origem',
      header: 'Origem',
      hideBelow: 'lg',
      render: (linha) =>
        linha.ip ? (
          <span title={linha.user_agent || undefined}>{linha.ip}</span>
        ) : (
          <span className="text-ink-400">nao registrada</span>
        ),
    },
    {
      key: 'conteudo',
      header: '',
      align: 'right',
      render: (linha) =>
        linha.states_hidden ? (
          <span
            className="text-2xs text-ink-500"
            title={motivoDoConteudoOculto(linha.states_hidden_reason)}
          >
            Conteudo restrito
          </span>
        ) : (
          <Button size="sm" variant="secondary" onClick={() => setAberta(linha)}>
            Ver alteracoes
          </Button>
        ),
    },
  ];

  const pagina = consulta.data;
  const filtrando = Object.values(aplicados).some(Boolean);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-col gap-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Filtros
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field
              label="Data inicial"
              type="date"
              value={rascunho.from}
              onChange={(evento) => muda('from')(evento.target.value)}
            />
            <Field
              label="Data final"
              type="date"
              value={rascunho.to}
              onChange={(evento) => muda('to')(evento.target.value)}
            />
            <Field
              label="Quem"
              placeholder="Nome ou e-mail, ou parte dele"
              value={rascunho.actor}
              disabled={!lePessoas}
              hint={
                lePessoas
                  ? undefined
                  : 'A busca por quem fez exige a leitura dos usuarios da empresa.'
              }
              onChange={(evento) => muda('actor')(evento.target.value)}
            />
            <Field
              label="Modulo"
              placeholder="operacoes, emissao..."
              value={rascunho.module}
              onChange={(evento) => muda('module')(evento.target.value)}
            />
            <Select
              label="Tipo de entidade"
              placeholder="Todos"
              options={opcoesDaEntidade}
              value={rascunho.entity_type}
              onChange={(evento) => muda('entity_type')(evento.target.value)}
            />
            <Field
              label="Identificador da entidade"
              placeholder="00000000-0000-0000-0000-000000000000"
              value={rascunho.entity_id}
              error={identificadorInvalido ? 'Identificador invalido.' : undefined}
              onChange={(evento) => muda('entity_id')(evento.target.value)}
            />
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setRascunho(filtrosVazios);
                setAplicados(filtrosVazios);
                setOffset(0);
              }}
            >
              Limpar filtro
            </Button>
            <Button onClick={aplicar} disabled={identificadorInvalido}>
              Aplicar
            </Button>
          </div>
        </CardBody>
      </Card>

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a trilha">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <div className="flex items-center justify-end border-b border-line px-4 py-2">
          <span className="text-xs text-ink-600">{pagina?.total ?? 0} registros</span>
        </div>
        <DataTable
          columns={colunas}
          items={pagina?.items ?? []}
          keyOf={(linha) => linha.id}
          loading={consulta.isPending}
          empty={
            <EmptyState
              icon="lock"
              title={filtrando ? 'Nenhum registro encontrado' : 'Trilha ainda vazia'}
              description={
                filtrando
                  ? 'Ajuste os filtros para ver outros registros.'
                  : 'Cada acao que altera dado passa a aparecer aqui.'
              }
            />
          }
        />
        {pagina ? (
          <Pagination
            total={pagina.total}
            limit={pagina.limit}
            offset={pagina.offset}
            onChange={setOffset}
          />
        ) : null}
      </Card>

      <HistoryChangeDialog row={aberta} onClose={() => setAberta(null)} />
    </div>
  );
}
